import React, { useState, useEffect } from 'react';

const NotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    // Simulate API call to fetch notifications
    setTimeout(() => {
      setNotifications([
        {
          id: 1,
          type: 'booking',
          title: 'Booking Confirmed',
          message: 'Your flight JS 1234 from JFK to LAX has been confirmed.',
          date: '2024-03-15T10:30:00Z',
          read: false,
          icon: '✈️'
        },
        {
          id: 2,
          type: 'reminder',
          title: 'Check-in Available',
          message: 'Online check-in is now available for your flight JS 1234.',
          date: '2024-03-14T08:00:00Z',
          read: false,
          icon: '🎫'
        },
        {
          id: 3,
          type: 'update',
          title: 'Flight Schedule Update',
          message: 'Your flight JS 5678 departure time has been updated to 15:30.',
          date: '2024-03-13T14:20:00Z',
          read: true,
          icon: '⏰'
        },
        {
          id: 4,
          type: 'promotion',
          title: 'Special Offer',
          message: 'Get 20% off your next booking with code SAVE20.',
          date: '2024-03-12T09:15:00Z',
          read: true,
          icon: '🎉'
        },
        {
          id: 5,
          type: 'security',
          title: 'Password Changed',
          message: 'Your account password was successfully changed.',
          date: '2024-03-10T16:45:00Z',
          read: true,
          icon: '🔒'
        }
      ]);
      setLoading(false);
    }, 1000);
  }, []);

  const handleMarkAsRead = (notificationId) => {
    setNotifications(prev => prev.map(notification => 
      notification.id === notificationId 
        ? { ...notification, read: true }
        : notification
    ));
  };

  const handleMarkAllAsRead = () => {
    setNotifications(prev => prev.map(notification => ({ ...notification, read: true })));
  };

  const handleDeleteNotification = (notificationId) => {
    setNotifications(prev => prev.filter(notification => notification.id !== notificationId));
  };

  const filteredNotifications = notifications.filter(notification => {
    if (filter === 'unread') return !notification.read;
    if (filter === 'read') return notification.read;
    return true;
  });

  const unreadCount = notifications.filter(n => !n.read).length;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInHours < 48) return 'Yesterday';
    return date.toLocaleDateString();
  };

  const getNotificationTypeColor = (type) => {
    switch (type) {
      case 'booking': return 'bg-jet-green-100 text-jet-green-800';
      case 'reminder': return 'bg-jet-blue-100 text-jet-blue-800';
      case 'update': return 'bg-jet-yellow-100 text-jet-yellow-800';
      case 'promotion': return 'bg-purple-100 text-purple-800';
      case 'security': return 'bg-jet-red-100 text-jet-red-800';
      default: return 'bg-jet-gray-100 text-jet-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-jet-yellow mx-auto mb-4"></div>
          <p className="text-jet-gray-600">Loading notifications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-jet-gray-900">Notifications</h1>
          <p className="text-jet-gray-600">
            {unreadCount > 0 ? `${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}` : 'All caught up!'}
          </p>
        </div>
        
        {unreadCount > 0 && (
          <button
            onClick={handleMarkAllAsRead}
            className="bg-jet-yellow hover:bg-jet-yellow-hover text-jet-gray-900 font-medium px-4 py-2 rounded-md transition-colors"
          >
            Mark All as Read
          </button>
        )}
      </div>

      {/* Filter Tabs */}
      <div className="bg-white rounded-lg shadow-sm mb-6">
        <div className="border-b border-jet-gray-200">
          <nav className="flex space-x-8 px-6">
            {[
              { id: 'all', label: 'All', count: notifications.length },
              { id: 'unread', label: 'Unread', count: unreadCount },
              { id: 'read', label: 'Read', count: notifications.length - unreadCount }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setFilter(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  filter === tab.id
                    ? 'border-jet-yellow text-jet-yellow'
                    : 'border-transparent text-jet-gray-500 hover:text-jet-gray-700 hover:border-jet-gray-300'
                }`}
              >
                {tab.label} ({tab.count})
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Notifications List */}
      <div className="bg-white rounded-lg shadow-sm">
        {filteredNotifications.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📭</div>
            <h3 className="text-lg font-medium text-jet-gray-900 mb-2">No notifications</h3>
            <p className="text-jet-gray-500">
              {filter === 'unread' ? 'All notifications have been read' : 'You have no notifications yet'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-jet-gray-200">
            {filteredNotifications.map((notification) => (
              <div
                key={notification.id}
                className={`p-6 hover:bg-jet-gray-50 transition-colors ${
                  !notification.read ? 'bg-jet-blue-50' : ''
                }`}
              >
                <div className="flex items-start space-x-4">
                  {/* Icon */}
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-jet-gray-100 rounded-full flex items-center justify-center text-lg">
                      {notification.icon}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <h3 className={`text-sm font-medium ${!notification.read ? 'text-jet-gray-900' : 'text-jet-gray-700'}`}>
                            {notification.title}
                          </h3>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getNotificationTypeColor(notification.type)}`}>
                            {notification.type}
                          </span>
                          {!notification.read && (
                            <div className="w-2 h-2 bg-jet-blue-500 rounded-full"></div>
                          )}
                        </div>
                        <p className="text-sm text-jet-gray-600 mb-2">
                          {notification.message}
                        </p>
                        <p className="text-xs text-jet-gray-500">
                          {formatDate(notification.date)}
                        </p>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center space-x-2 ml-4">
                        {!notification.read && (
                          <button
                            onClick={() => handleMarkAsRead(notification.id)}
                            className="text-jet-blue-600 hover:text-jet-blue-700 text-sm font-medium"
                          >
                            Mark as read
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteNotification(notification.id)}
                          className="text-jet-red-600 hover:text-jet-red-700 text-sm"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Notification Settings */}
      <div className="bg-white rounded-lg shadow-sm p-6 mt-6">
        <h3 className="text-lg font-semibold text-jet-gray-900 mb-4">Notification Settings</h3>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium text-jet-gray-900">Email Notifications</div>
              <div className="text-sm text-jet-gray-600">Receive notifications via email</div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" className="sr-only peer" defaultChecked />
              <div className="w-11 h-6 bg-jet-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-jet-yellow-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-jet-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-jet-yellow"></div>
            </label>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium text-jet-gray-900">SMS Notifications</div>
              <div className="text-sm text-jet-gray-600">Receive notifications via SMS</div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" className="sr-only peer" />
              <div className="w-11 h-6 bg-jet-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-jet-yellow-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-jet-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-jet-yellow"></div>
            </label>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium text-jet-gray-900">Push Notifications</div>
              <div className="text-sm text-jet-gray-600">Receive push notifications in browser</div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" className="sr-only peer" defaultChecked />
              <div className="w-11 h-6 bg-jet-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-jet-yellow-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-jet-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-jet-yellow"></div>
            </label>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium text-jet-gray-900">Marketing Emails</div>
              <div className="text-sm text-jet-gray-600">Receive promotional offers and updates</div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" className="sr-only peer" />
              <div className="w-11 h-6 bg-jet-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-jet-yellow-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-jet-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-jet-yellow"></div>
            </label>
          </div>
        </div>

        <div className="mt-6 pt-4 border-t border-jet-gray-200">
          <button className="bg-jet-yellow hover:bg-jet-yellow-hover text-jet-gray-900 font-medium px-4 py-2 rounded-md transition-colors">
            Save Settings
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationsPage;